"use client";

import { ReactNode } from "react";

import { usePathname } from "next/navigation";

import useRibbonHeight from "@/hooks/useRibbonHeight";
import { LayoutData } from "@/types/layoutData";
import { getLayoutDataBySlug } from "@/utils/getLayoutDataBySlug";
import { getStickyBottomBarClearanceClass } from "@/utils/stickyBottomBar";
import { currentTheme } from "@/utils/theming";

interface MainFactoryProps {
  children: ReactNode;
  allContentfulLayoutData: LayoutData[];
}

export default function Index({ children, allContentfulLayoutData }: MainFactoryProps) {
  const pathname = usePathname();
  /*
   * Keeps --ribbon-height in sync for sticky/fixed chrome. The default mobile header
   * is in-flow (exact ribbon height), so we no longer inject a measured spacer here.
   */
  useRibbonHeight();
  const layoutDataBySlug = getLayoutDataBySlug(pathname, allContentfulLayoutData);
  const shouldHideAttentivePopupFromUrl =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("hide_attentive_popup") === "true";

  if (
    (layoutDataBySlug?.hide_attentive_popup || shouldHideAttentivePopupFromUrl) &&
    typeof document !== "undefined"
  ) {
    document.cookie = `hide_attentive_popup=true; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=${pathname};`;
  }

  const isAppTheme = currentTheme(pathname) === "app";
  const reformattedPathname = pathname?.split("/").join("-") || "";
  const hasMainTopPadding = layoutDataBySlug?.has_main_padding !== false;
  /*
   * FooterFactory normally supplies clearance below it for pages with a
   * sticky bottom action bar (checkout/quiz results/cart), but it renders
   * nothing at all in webview/app theme — so `<main>`, which always
   * renders, must supply that same clearance itself in that case, since it
   * becomes the trailing element on the page instead. See
   * src/utils/stickyBottomBar.ts and Footer/index.tsx.
   */
  const ownStickyBarClearanceClass = isAppTheme
    ? getStickyBottomBarClearanceClass(pathname)
    : undefined;

  return (
    <main
      className={`
          body-wrapper
          flex-1
          ${reformattedPathname || "index"}
          ${hasMainTopPadding && !isAppTheme ? "main-top-padding" : ""}
          ${isAppTheme ? "app-webview" : ""}
          ${ownStickyBarClearanceClass || ""}
          ${layoutDataBySlug?.header_background_color || "White"}
        `
        .trim()
        .replace(/\s+/g, " ")}
    >
      {children}
    </main>
  );
}
